'use client';

// Custom hook for integrating with Sitecore Search Cloud SDK.
import { useState, useCallback, useRef, useTransition } from 'react';
import {
  Context,
  getWidgetData,
  SearchWidgetItem,
  WidgetRequestData,
  SearchSuggestionOptions,
  ComparisonFilter,
  LogicalFilter,
  ComparisonFacetFilter,
} from '@sitecore-cloudsdk/search/browser';
import {
  ensureCloudSdkBrowserForSearch,
  type CloudSdkBrowserSearchSettings,
} from 'lib/search/ensure-cloud-sdk-browser';
import { mapResultsToTrackEntities, trackSearchWidgetView } from 'lib/search/analytics';

/** Matches Cloud SDK facet `types` item shape (SDK does not export this from `browser`). */
type FacetTypeOptions = {
  name: string;
  filter?: {
    type: string;
    values: unknown[];
  };
};

export interface ExpertSearchResult {
  id: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  job_title?: string;
  picture?: string;
  quote?: string;
  expertise?: string[];
  location?: string[];
  linked_in_url?: string;
  url?: string;
  [key: string]: unknown;
}

export interface FacetValue {
  id: string;
  text: string;
  count: number;
}

export interface Facet {
  name: string;
  label: string;
  value: FacetValue[];
}

export interface ExpertSearchData {
  content: ExpertSearchResult[];
  facet: Facet[];
  total_item: number;
  limit: number;
  offset: number;
}

export interface FilterItem {
  name: string;
  filter: {
    type: string;
    values: string[];
  };
}

export interface SearchFilter {
  all?: boolean;
  types?: FilterItem[];
}

export interface SearchConfig {
  widgetType?: string;
  widgetId?: string;
  maxResults?: number;
  locale?: {
    language: string;
    country: string;
  };
  sources?: string[];
  offset?: number;
  filter?: SearchFilter | ComparisonFilter | LogicalFilter<'and' | 'or'>;
  facetFilters?: {
    [facetName: string]: string;
  };
  /** Edge context + site for Cloud SDK init before `getWidgetData` (avoids racing Bootstrap). */
  cloudSdkSearch?: Partial<CloudSdkBrowserSearchSettings>;
}

export interface Suggestion {
  text: string;
  freq: number;
}

function resolveSearchSources(config: SearchConfig): [string, ...string[]] | undefined {
  const fromConfig = config.sources?.filter((id): id is string => Boolean(id));
  if (fromConfig && fromConfig.length > 0) {
    return fromConfig as [string, ...string[]];
  }
  const fromEnv = process.env.NEXT_PUBLIC_SEARCH_EXPERT_PROFILES_SOURCE_ID;
  return fromEnv ? [fromEnv] : undefined;
}

function strVal(v: unknown): string | undefined {
  if (v === null || v === undefined) return undefined;
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return undefined;
}

/** Sitecore Search sometimes returns rows under nested keys instead of a bare array. */
function extractContentRows(raw: unknown): unknown[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      return extractContentRows(JSON.parse(raw) as unknown);
    } catch {
      return [];
    }
  }
  if (typeof raw !== 'object') return [];
  const o = raw as Record<string, unknown>;
  for (const key of ['items', 'hits', 'results', 'documents', 'experts', 'data', 'records']) {
    const v = o[key];
    if (Array.isArray(v)) return v;
  }
  const nestedArrays = Object.values(o).filter(
    (v): v is unknown[] => Array.isArray(v) && v.length > 0 && typeof v[0] === 'object'
  );
  if (nestedArrays.length === 1) return nestedArrays[0];
  if (nestedArrays.length > 1) return nestedArrays.flat();
  return [];
}

function contentRowCount(widget: Record<string, unknown>): number {
  return extractContentRows(widget.content).length;
}

function unwrapWidgets(apiData: unknown): unknown[] {
  if (!apiData || typeof apiData !== 'object') return [];
  const root = apiData as Record<string, unknown>;
  if (Array.isArray(root.widgets)) return root.widgets;
  const nested = root.data;
  if (nested && typeof nested === 'object') {
    const w = (nested as Record<string, unknown>).widgets;
    if (Array.isArray(w)) return w;
  }
  return [];
}

function pickExpertWidget(widgets: unknown[], widgetId: string): Record<string, unknown> | null {
  const list = widgets.filter((w): w is Record<string, unknown> => !!w && typeof w === 'object');
  if (!list.length) return null;
  const byId = list.find(
    (w) =>
      w.rfk_id === widgetId ||
      w.widget_id === widgetId ||
      w.widgetId === widgetId ||
      w.rfkId === widgetId
  );
  if (byId) return byId;
  return list.reduce((best, cur) => (contentRowCount(cur) > contentRowCount(best) ? cur : best));
}

function unwrapRecordRow(row: unknown): unknown {
  if (!row || typeof row !== 'object') return row;
  const o = row as Record<string, unknown>;
  if (o.item && typeof o.item === 'object' && !Array.isArray(o.item)) {
    return o.item;
  }
  if (o.document && typeof o.document === 'object' && !Array.isArray(o.document)) {
    return o.document;
  }
  return row;
}

/** Map API / index field names to what `ExpertList` UI expects. */
function normalizeExpertRow(raw: unknown, fallbackIndex = 0): ExpertSearchResult {
  if (!raw || typeof raw !== 'object') {
    return { id: `expert-${fallbackIndex}` };
  }
  const r = raw as Record<string, unknown>;

  const urlFromLink =
    typeof r.url === 'object' && r.url !== null
      ? strVal((r.url as Record<string, unknown>).href)
      : undefined;

  const first_name =
    strVal(r.first_name) ||
    strVal(r.firstName) ||
    strVal(r.firstname) ||
    strVal(r.given_name) ||
    '';

  const last_name =
    strVal(r.last_name) || strVal(r.lastName) || strVal(r.lastname) || strVal(r.family_name) || '';

  let name =
    strVal(r.name) ||
    strVal(r.title) ||
    strVal(r.display_name) ||
    strVal(r.full_name) ||
    '';

  if (!name.trim() && (first_name || last_name)) {
    name = `${first_name} ${last_name}`.trim();
  }

  const job_title =
    strVal(r.job_title) ||
    strVal(r.jobTitle) ||
    strVal(r.jobtitle) ||
    strVal(r.subtitle) ||
    strVal(r.role) ||
    '';

  let picture =
    strVal(r.picture) ||
    strVal(r.image_url) ||
    strVal(r.imageUrl) ||
    strVal(r.thumbnail_url) ||
    strVal(r.photo_url);

  if (!picture && r.image && typeof r.image === 'object') {
    const img = r.image as Record<string, unknown>;
    picture = strVal(img.src) || strVal(img.url) || strVal(img.href);
  }

  const quote = strVal(r.quote) || strVal(r.description) || strVal(r.summary);
  const linked_in_url =
    strVal(r.linked_in_url) ||
    strVal(r.linkedInURL) ||
    strVal(r.linkedin_url) ||
    strVal(r.linkedInUrl) ||
    strVal(r.linkedin);

  const url = strVal(r.url) || urlFromLink;

  const id =
    strVal(r.id) ||
    strVal(r.item_id) ||
    strVal(r.document_id) ||
    strVal(r.source_id) ||
    url ||
    `expert-${fallbackIndex}`;

  return {
    ...r,
    id,
    name,
    first_name: first_name || (name ? name.split(/\s+/)[0] : ''),
    last_name: last_name || (name ? name.split(/\s+/).slice(1).join(' ') : ''),
    job_title,
    picture,
    quote,
    linked_in_url,
    url,
  };
}

function normalizeFacets(raw: unknown): Facet[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((f): Facet => {
    if (!f || typeof f !== 'object') {
      return { name: '', label: '', value: [] };
    }
    const obj = f as Record<string, unknown>;
    const name = String(obj.name ?? '');
    const label = String(obj.label ?? name);
    let values = obj.value;
    if (!Array.isArray(values) && Array.isArray(obj.values)) {
      values = obj.values;
    }
    if (!Array.isArray(values)) {
      return { name, label, value: [] };
    }
    return {
      name,
      label,
      value: values.map((v, i) => {
        if (!v || typeof v !== 'object') {
          return { id: String(i), text: '', count: 0 };
        }
        const item = v as Record<string, unknown>;
        return {
          id: String(item.id ?? item.text ?? i),
          text: String(item.text ?? item.name ?? item.label ?? ''),
          count: Number(item.count ?? 0),
        };
      }),
    };
  });
}

export interface UseCloudSearchReturn {
  data: ExpertSearchData | null;
  loading: boolean;
  /** True after the first `performSearch` finishes (success, error, or early exit). Drives initial skeletons. */
  initialSearchSettled: boolean;
  error: string | null;
  suggestions: Suggestion[];
  loadingSuggestions: boolean;
  performSearch: (query?: string, customConfig?: Partial<SearchConfig>) => Promise<void>;
  getSuggestions: (query: string) => Promise<void>;
  clearSuggestions: () => void;
}

export const useCloudSearch = (initialConfig: SearchConfig): UseCloudSearchReturn => {
  const [data, setData] = useState<ExpertSearchData | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialSearchSettled, setInitialSearchSettled] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  // Wrap result-applying state updates (setData, setLoading(false), etc.) in
  // a transition so the parent <Suspense> boundary doesn't fall back to its
  // empty fallback while the new tree commits. Without this, even with
  // callers using their own startTransition, the post-`await` state updates
  // here are urgent and can trigger the Suspense fallback, causing the
  // expert list to flash empty between searches.
  const [, startResultTransition] = useTransition();

  // Keep config current without making it a useCallback dependency (prevents cascade re-renders)
  const configRef = useRef(initialConfig);
  configRef.current = initialConfig;

  const performSearch = useCallback(
    async (query?: string, customConfig?: Partial<SearchConfig>) => {
      setLoading(true);
      setError(null);

      try {
        const config = { ...configRef.current, ...customConfig };
        const sources = resolveSearchSources(config);
        if (!sources?.length) {
          startResultTransition(() => {
          setError(
            'Expert search is not configured. Set NEXT_PUBLIC_SEARCH_EXPERT_PROFILES_SOURCE_ID or the source field in Sitecore.'
          );
          setData(null);
          });
          return;
        }

        await ensureCloudSdkBrowserForSearch({
          sitecoreEdgeContextId: config.cloudSdkSearch?.sitecoreEdgeContextId,
          siteName: config.cloudSdkSearch?.siteName,
          sitecoreEdgeUrl: config.cloudSdkSearch?.sitecoreEdgeUrl,
        });

        // Build facet types from facetFilters if provided
        // This approach filters results AND keeps the selected facet showing full counts
        const facetTypeList = (() => {
          if (!config.facetFilters || Object.keys(config.facetFilters).length === 0) {
            return undefined;
          }

          const filtered = Object.entries(config.facetFilters)
            .filter(([, value]) => value) // Only include non-empty values
            .map(([name, value]) => ({
              name,
              filter: {
                type: 'or',
                values: [new ComparisonFacetFilter('eq', value)],
              },
            }));

          // Only return if we have at least one filter, otherwise undefined
          return filtered.length > 0
            ? (filtered as [FacetTypeOptions, ...FacetTypeOptions[]])
            : undefined;
        })();

        const searchOptions: Record<string, unknown> = {
          content: {},
          limit: config.maxResults || 100,
          offset: config.offset || 0,
          facet: {
            all: true,
            types: facetTypeList,
          },
          // Add suggestions configuration - trying different block names
          suggestion: [
            {
              name: 'expert_list_auto_suggester', // Try with underscores first
              max: 10,
              keyphraseFallback: true,
            },
          ] as [SearchSuggestionOptions, ...SearchSuggestionOptions[]],
        };

        // Add query with keyphrase if provided
        if (query && query.trim()) {
          searchOptions.query = {
            keyphrase: query.trim(),
          };
        }

        // Do NOT use search filter when using facetFilters
        // The facet.types approach handles both filtering and facet behavior
        if (config.filter && !config.facetFilters) {
          searchOptions.filter = config.filter;
        }

        const searchWidget = new SearchWidgetItem(
          'expertprofile', // Match the CEC configuration
          config.widgetId || 'rfkid_expert_profile',
          searchOptions,
          sources
        );

        // Facets are now configured in searchOptions

        const widgetRequest = new WidgetRequestData([searchWidget]);

        // Create a new context for each request like in the example
        const requestContext = new Context({
          locale: config.locale || {
            language: 'en',
            country: 'us',
          },
        });

        const apiData = await getWidgetData(widgetRequest, requestContext);

        // Log any errors from the API
        if (
          (apiData as unknown as Record<string, unknown>)?.errors &&
          Array.isArray((apiData as unknown as Record<string, unknown>).errors)
        ) {
          const errors = (apiData as unknown as Record<string, unknown>).errors as Record<
            string,
            unknown
          >[];
          console.error('❌ API Errors:', errors);
          errors.forEach((error: Record<string, unknown>, index: number) => {
            console.error(`❌ Error ${index + 1}:`, error);
          });
        }

        const widgets = unwrapWidgets(apiData);

        // Extract the search results from the API response (shape varies by widget / API version).
        // Compute the next data shape synchronously, then commit it inside a
        // transition together with loading/initial flags so the parent Suspense
        // boundary doesn't briefly fall back to its empty fallback.
        let nextData: ExpertSearchData;
        if (widgets.length > 0) {
          const widgetId = config.widgetId || 'rfkid_expert_profile';
          const widgetData =
            pickExpertWidget(widgets, widgetId) ?? (widgets[0] as Record<string, unknown>);

          const rows = extractContentRows(widgetData.content);
          const content = rows
            .map(unwrapRecordRow)
            .map((row, i) => normalizeExpertRow(row, i));

          nextData = {
            content,
            facet: normalizeFacets(widgetData.facet),
            total_item: Number(widgetData.total_item) || content.length,
            limit: Number(widgetData.limit) || config.maxResults || 100,
            offset: Number(widgetData.offset) || 0,
          };
        } else {
          nextData = {
            content: [],
            facet: [],
            total_item: 0,
            limit: config.maxResults || 100,
            offset: 0,
          };
        }

        startResultTransition(() => {
          setData(nextData);
          setLoading(false);
          setInitialSearchSettled(true);
        });

        const widgetId = config.widgetId || 'rfkid_expert_profile';
        void trackSearchWidgetView({
          widgetId,
          entities: mapResultsToTrackEntities(
            nextData.content.map((item) => ({
              id: item.id,
              url: typeof item.url === 'string' ? item.url : undefined,
            })),
            'expertprofile'
          ),
          request: {
            keyword: query?.trim() || undefined,
            numResults: nextData.content.length,
            totalResults: nextData.total_item,
            numRequested: config.maxResults || nextData.limit,
            pageSize: nextData.limit,
            pageNumber:
              nextData.limit > 0 ? Math.floor(nextData.offset / nextData.limit) + 1 : 1,
          },
        });
      } catch (err) {
        console.error('Cloud SDK Search Error:', err);
        startResultTransition(() => {
        setError(err instanceof Error ? err.message : 'An error occurred during search');
        setData(null);
        setLoading(false);
        setInitialSearchSettled(true);
        });
      }
    },
    []
  );

  const fetchSuggestions = useCallback(
    async (query: string) => {
      if (!query || query.length < 2 || query.trim() === '') {
        setSuggestions([]);
        return;
      }

      setLoadingSuggestions(true);

      try {
        const cfg = configRef.current;
        if (!cfg.widgetId) {
          setSuggestions([]);
          setLoadingSuggestions(false);
          return;
        }

        const sources = resolveSearchSources(cfg);
        if (!sources?.length) {
          setSuggestions([]);
          setLoadingSuggestions(false);
          return;
        }

        await ensureCloudSdkBrowserForSearch({
          sitecoreEdgeContextId: cfg.cloudSdkSearch?.sitecoreEdgeContextId,
          siteName: cfg.cloudSdkSearch?.siteName,
          sitecoreEdgeUrl: cfg.cloudSdkSearch?.sitecoreEdgeUrl,
        });

        // Create a widget request specifically for suggestions (no search results)
        const searchOptions: Record<string, unknown> = {
          suggestion: [
            {
              name: 'expert_list_auto_suggester',
              max: 20,
              keyphraseFallback: true,
            },
          ] as [SearchSuggestionOptions, ...SearchSuggestionOptions[]],
          limit: 20,
          offset: 0,
          query: { keyphrase: query },
          facet: { all: true },
          content: {},
        };

        const searchWidget = new SearchWidgetItem(
          cfg.widgetType || 'expertprofile',
          cfg.widgetId || 'rfkid_expert_profile',
          searchOptions,
          sources
        );

        const widgetRequest = new WidgetRequestData([searchWidget]);

        const requestContext = new Context({
          locale: cfg.locale || {
            language: 'en',
            country: 'us',
          },
        });

        const apiData = await getWidgetData(widgetRequest, requestContext);

        // Extract suggestions from the response (content may be nested like main search).
        const suggestionWidgets = unwrapWidgets(apiData);
        if (suggestionWidgets.length > 0) {
          const widgetId = cfg.widgetId || 'rfkid_expert_profile';
          const widgetDataRecord =
            pickExpertWidget(suggestionWidgets, widgetId) ??
            (suggestionWidgets[0] as Record<string, unknown>);

          const suggestionBlock = widgetDataRecord.suggestion as Record<string, unknown> | undefined;
          const fromNamed =
            (Array.isArray(suggestionBlock?.expert_list_auto_suggester)
              ? suggestionBlock.expert_list_auto_suggester
              : undefined) ??
            (Array.isArray(widgetDataRecord.expert_list_auto_suggester)
              ? widgetDataRecord.expert_list_auto_suggester
              : undefined) ??
            (Array.isArray(suggestionBlock?.suggestion) ? suggestionBlock.suggestion : undefined);
          const suggestionRows = Array.isArray(fromNamed)
            ? fromNamed
            : extractContentRows(widgetDataRecord.content);

          if (suggestionRows.length > 0) {
            setSuggestions(
              suggestionRows.map((item: unknown) => {
                const row = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
                const text =
                  strVal(row.text) ||
                  strVal(row.name) ||
                  strVal(row.title) ||
                  strVal(row.keyphrase) ||
                  '';
                return { text, freq: Number(row.freq ?? row.count ?? 0) };
              })
            );
          } else {
            setSuggestions([]);
          }
        }
      } catch (err) {
        console.error('❌ Suggestions error:', err);
        setSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    },
    []
  );

  // Add a function to clear suggestions
  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  return {
    data,
    loading,
    initialSearchSettled,
    error,
    suggestions,
    loadingSuggestions,
    performSearch,
    getSuggestions: fetchSuggestions,
    clearSuggestions,
  };
};
